import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PlannerConfigEntityWithOptionalFields } from '@/services/configurator/PlannerConfigTypes';
import { Control } from 'react-hook-form';
import { Lock, Unlock } from 'lucide-react';
import { useUser } from '@/components/providers/user-provider';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export type PhraseFormFieldProps = {
  phraseKey: string;
  phraseFormControl: Control<
    PlannerConfigEntityWithOptionalFields,
    unknown,
    PlannerConfigEntityWithOptionalFields
  >;
  disabled?: boolean;
  className?: string;
  syncedPhraseKeys?: Set<string>;
};

export function PhraseFormField({
  phraseKey,
  phraseFormControl,
  disabled,
  className,
  syncedPhraseKeys,
}: PhraseFormFieldProps) {
  const user = useUser();
  const isAdmin = user.role === 'admin';
  const isKeyLocked = syncedPhraseKeys?.has(phraseKey) ?? false;
  const isDisabled = disabled || (isKeyLocked && !isAdmin);

  return (
    <div className={className}>
      <FormField
        control={phraseFormControl}
        name={`phraseStrings.${phraseKey}`}
        disabled={isDisabled}
        render={({ field }) => (
          <FormItem>
            <FormLabel className='flex items-center gap-2'>
              {phraseKey}
              {isKeyLocked && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {isAdmin ? (
                        <Unlock className='h-4 w-4 text-muted-foreground' />
                      ) : (
                        <Lock className='h-4 w-4 text-muted-foreground' />
                      )}
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        This field is read-only. Edit directly in Phrase if
                        changes are needed.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                disabled={isDisabled}
                value={field.value || ''}
                onBlur={(e) => {
                  field.onBlur();
                  field.onChange(e.target.value.trim().replace(/\s+/g, ' '));
                }}
                className={
                  isKeyLocked && !isAdmin ? 'bg-muted cursor-not-allowed' : ''
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
